import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { EmojiProvider } from '@atlaskit/emoji';
import { Field } from '@atlaskit/form';
import type { MentionProvider } from '@atlaskit/mention';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import InlineErrorMessage from '@atlassian/jira-project-settings-apps-common/src/inline-error-message/view.tsx';
import type { FieldConfigurationComponentProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import type { AssociatedField } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import type { ValidationError } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/validation.tsx';
import { AkFieldWrapper } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/styled.tsx';
import { POSITION_FIELD_DEFAULT_VALUE_INPUT } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/constants.tsx';
import { AsyncEditor, type Props as AsyncEditorProps } from './async-editor/index.tsx';
import messages from './messages.tsx';
import { isAdfEmpty } from './utils.tsx';

type FilteredValidationError = ValidationError & {
	position: typeof POSITION_FIELD_DEFAULT_VALUE_INPUT;
};

function isRelevantError(e: ValidationError): e is FilteredValidationError {
	return e.position === POSITION_FIELD_DEFAULT_VALUE_INPUT;
}

function getFilteredErrors(errors: ReadonlyArray<ValidationError>): FilteredValidationError[] {
	return errors.filter(isRelevantError);
}

const DefaultValueErrors = ({
	filteredErrors,
}: {
	filteredErrors: ReadonlyArray<FilteredValidationError>;
}) => {
	const intl = useIntl();
	if (filteredErrors.length === 0) {
		return null;
	}
	return (
		<section>
			{filteredErrors.map((e) => (
				<InlineErrorMessage
					key={e.messageDescriptor.id}
					message={intl.formatMessage(e.messageDescriptor, e.values)}
				/>
			))}
		</section>
	);
};

const getDefaultLabel = (field: AssociatedField): MessageDescriptor => {
	switch (field.type) {
		case DESCRIPTION_FIELD_TYPE:
			return messages.textDefaultDescriptionLabel;

		case ENVIRONMENT_FIELD_TYPE:
			return messages.environmentDefaultValueLabel;

		default:
			return messages.textDefaultValueLabel;
	}
};

const getDisabledTooltipMessage = (field: AssociatedField): MessageDescriptor => {
	switch (field.type) {
		case DESCRIPTION_FIELD_TYPE:
			return messages.descriptionDefaultValueDisabledTooltip;
		case ENVIRONMENT_FIELD_TYPE:
			return messages.environmentDefaultValueDisabledTooltip;
		default:
			return messages.textDefaultValueDisabledTooltip;
	}
};

function refineDefaultValue(defaultValue: unknown): ADFEntity | undefined {
	/*
	 * ADFs have many more properties in them but type: string is the only one that is required.
	 * I wish there was some utility method to do this but as of now there isn't any.
	 * I checked adf-utils and adf-schema but none export functions like this.
	 *
	 * (adf-utils has a validator but that one accepts `ADFEntity`.)
	 */
	if (
		typeof defaultValue === 'object' &&
		defaultValue != null &&
		'type' in defaultValue &&
		typeof defaultValue.type === 'string'
	) {
		return {
			...defaultValue,
			type: defaultValue.type,
		};
	}
	return undefined;
}

export type Props = FieldConfigurationComponentProps & {
	readonly mentionProvider: Promise<MentionProvider> | undefined;
	readonly emojiProvider: Promise<EmojiProvider> | undefined;
};

export const MultilineView = ({
	field,
	onUpdate,
	fieldErrors,
	testId,
	tooltipDelay,
	...rest
}: Props) => {
	const intl: IntlShape = useIntl();
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;
	const onEditorUpdate = useCallback(
		(adf: ADFEntity): void => {
			if (isAdfEmpty(adf)) {
				onUpdate({
					...field,
					defaultValue: null,
				});
			} else {
				onUpdate({
					...field,
					defaultValue: adf,
				});
			}
		},
		[field, onUpdate],
	);

	const filteredErrors = getFilteredErrors(fieldErrors);
	const isValid = filteredErrors.length === 0;

	const defaultValue: ADFEntity | undefined = refineDefaultValue(field.defaultValue);
	const editorProps: AsyncEditorProps = {
		...rest,
		onUpdate: onEditorUpdate,
		disabled: isModifyDefaultValueDisabled,
		defaultValue,
		mentionProvider: undefined,
	};

	return (
		<DefaultValueStyled>
			{/*
			 * So usually the error message is _below_ the element it relates to, but this one is special!
			 * Because:
			 * - it is the only one that displays asynchronously, after clicking on "Save" (that's because it is too expensive to do in real time)
			 * - and after clicking "Save", the page opens the field that has the error, and scrolls to it, BUT
			 * - it does not scroll the contents of the field.
			 * And because the only error here is "The text is too long" then it means the message will always be hidden behind the fold.
			 * We could also scroll to the bottom of the expanded field configuration but ... it's just easier to move the message up.
			 */}
			{!isValid && <DefaultValueErrors filteredErrors={filteredErrors} />}
			<AkFieldWrapper>
				<Field
					name={`${field.key}-default-value`}
					label={intl.formatMessage(getDefaultLabel(field))}
				>
					{() => (
						<Tooltip
							content={
								isModifyDefaultValueDisabled
									? intl.formatMessage(getDisabledTooltipMessage(field))
									: null
							}
							delay={tooltipDelay}
						>
							{/* We need this wrapping div to be used as a reference element for tooltip positioning */}
							<div data-testid={testId}>
								<AsyncEditor {...editorProps} />
							</div>
						</Tooltip>
					)}
				</Field>
			</AkFieldWrapper>
		</DefaultValueStyled>
	);
};

/*
 * react-beautiful-dnd overrides the cursor to grab, but there is a contenteditable div from
 * atlaskit/editor that has text selection instead of grabbing. So let's override the cursor here too.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultValueStyled = styled.div({
	paddingRight: token('space.500'),
	paddingLeft: token('space.500'),
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[contenteditable]': {
		cursor: 'text',
	},
});
